import { FocusTrap } from 'focus-trap';
import FocusTrapQueue from '../../tnew/ui/FocusTrapQueue';
import SyosMediaQuery from './SyosMediaQuery';
import SyosEvents from './SyosEvents';
import type SyosController from '../SyosController';
import type { ScreenUpdatedEventArgs } from '../SyosTypes';

export default class SyosMobileFocusTrapper {
  private focusTrap?: FocusTrap;

  private readonly onMobileChanged = () => {
    if (this.isMobile && !this.isTrapped) { this.trapFocus(); }
    if (!this.isMobile && this.isTrapped) { this.freeFocus(); }
  };

  private readonly onSeatDataLoaded = () => {
    // ! The order here is important to avoid concurrency issues, the call to `trapFocus` MUST happen before subscribing
    // ! to changes in screen width.
    this.trapFocus();
    this.subscribeMobileChanges();
  };

  private readonly onScreenUpdated = ({ screen }: ScreenUpdatedEventArgs) => {
    if (screen === undefined) {
      // ! The order here is important to avoid concurrency issues, the call to `unsubscribeMobileChanges` MUST happen
      // ! before freeing the focus.
      this.unsubscribeMobileChanges();
      this.freeFocus();
    }
  };

  constructor(private syosController: SyosController, private element: HTMLElement, private syosMediaQuery: SyosMediaQuery) {
    syosController.on(SyosEvents.SEAT_LIST_DATA_LOADED, this.onSeatDataLoaded);
    syosController.on(SyosEvents.SCREEN_UPDATED, this.onScreenUpdated);
  }

  get isTrapped() { return this.focusTrap !== undefined; }

  get isMobile() { return this.syosMediaQuery.testIsMobile(); }

  trapFocus() {
    if (!this.isMobile) { return; }
    if (this.isTrapped) { this.freeFocus(); }

    this.focusTrap = FocusTrapQueue.createFocusTrap(this.element, {
      escapeDeactivates: false,
      fallbackFocus: this.element,
    }).activate();
  }

  freeFocus() {
    this.focusTrap = this.focusTrap?.deactivate();
    this.focusTrap = undefined;
  }

  subscribeMobileChanges() {
    this.syosMediaQuery.on(SyosEvents.MOBILE_ENTER, this.onMobileChanged);
    this.syosMediaQuery.on(SyosEvents.MOBILE_LEAVE, this.onMobileChanged);
  }

  unsubscribeMobileChanges() {
    this.syosMediaQuery.removeListener(SyosEvents.MOBILE_ENTER, this.onMobileChanged);
    this.syosMediaQuery.removeListener(SyosEvents.MOBILE_LEAVE, this.onMobileChanged);
  }

  destroy() {
    // ! The order here is important to avoid concurrency issues, any events fired asynchronously while this function
    // ! runs must not affect the final state of not trapping the focus.
    this.unsubscribeMobileChanges();
    this.syosController.removeListener(SyosEvents.SEAT_LIST_DATA_LOADED, this.onSeatDataLoaded);
    this.syosController.removeListener(SyosEvents.SCREEN_UPDATED, this.onScreenUpdated);
    this.freeFocus();
  }
}
